import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, FileText, CheckCircle, AlertTriangle, Clock, Trash2, ArrowRightLeft } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { InvestmentForm } from '../../components/forms/InvestmentForm';
import { InvestorForm } from '../../components/forms/InvestorForm';
import { api } from '../../services/api';
import { HistoryTable } from '../../components/history/HistoryTable';
import { InvestorAccountStatementsTab } from '../../components/investors/InvestorAccountStatementsTab';
import { InvestorRoiTab } from '../../components/investors/InvestorRoiTab';
import { DocumentViewer } from '../../components/documents/DocumentViewer';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { InvestorSelect } from '../../components/ui/InvestorSelect';
import { Toast } from '../../components/ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { Investment, Investor, Property } from '../../types';
import {
  formatDate,
  formatDateSpanishLong,
  formatDateTime,
  formatFechaCorta as formatFechaCortaUtil,
} from '../../utils/date';
import { mapTimelineToChangeLogEntries, mergeHistoryRows } from '../../utils/historyRows';
import { getViteApiUrl } from '../../config/env';

const DOC_EXPIRY_WARNING_DAYS = 30;
type DocStatus = 'vigente' | 'por_vencer' | 'vencido' | 'sin_fecha';
function getDocumentStatus(expiresAt: string | undefined): DocStatus {
  if (!expiresAt) return 'sin_fecha';
  const exp = new Date(expiresAt);
  exp.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'vencido';
  if (daysLeft <= DOC_EXPIRY_WARNING_DAYS) return 'por_vencer';
  return 'vigente';
}
function DocumentStatusBadge({ status }: { status: DocStatus }) {
  const config: Record<DocStatus, { label: string; className: string; Icon: React.ComponentType<{ className?: string }> }> = {
    vigente: { label: 'Vigente', className: 'bg-green-100 text-green-800', Icon: CheckCircle },
    por_vencer: { label: 'Por vencer', className: 'bg-amber-100 text-amber-800', Icon: Clock },
    vencido: { label: 'Vencido', className: 'bg-red-100 text-red-800', Icon: AlertTriangle },
    sin_fecha: { label: 'Sin fecha', className: 'bg-gray-100 text-gray-600', Icon: FileText },
  };
  const { label, className, Icon } = config[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

export const InvestorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<
    'general' | 'investments' | 'documents' | 'statements' | 'roi' | 'history'
  >('general');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditInvestmentModalOpen, setIsEditInvestmentModalOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [investmentSubmitLoading, setInvestmentSubmitLoading] = useState(false);
  const [investorEditLoading, setInvestorEditLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [transferringInvestment, setTransferringInvestment] = useState<Investment | null>(null);
  const [targetInvestorId, setTargetInvestorId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');
  const canDeleteInvestors = hasPermission('delete_investors');
  const [pendingDeleteDocument, setPendingDeleteDocument] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [documentDeleteLoading, setDocumentDeleteLoading] = useState(false);
  const [addDocModalOpen, setAddDocModalOpen] = useState(false);
  const [addDocForm, setAddDocForm] = useState({ title: '', fileType: '', expiresAt: '' });
  const [addDocFile, setAddDocFile] = useState<File | null>(null);
  const [addDocLoading, setAddDocLoading] = useState(false);
  const [addDocError, setAddDocError] = useState<string | null>(null);

  const { data: propertiesData } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
  });
  const properties: Property[] = propertiesData?.data ?? [];

  const { data: investorsListData } = useQuery({
    queryKey: ['investors', 'list-transfer'],
    queryFn: () => api.getInvestors({ limit: 500 }),
    staleTime: 60_000,
    enabled: transferringInvestment !== null,
  });
  const otherInvestors: Investor[] = ((investorsListData as any)?.data ?? []).filter(
    (inv: any) => inv.id !== id,
  );

  const { data: investorData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['investor', id],
    queryFn: () => api.getInvestor(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: roiSummary } = useQuery({
    queryKey: ['investor-roi', id],
    queryFn: () => api.getInvestorROI(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: investorTimeline = [], isLoading: investorTimelineLoading } = useQuery({
    queryKey: ['investor-timeline', id],
    queryFn: () => api.getInvestorTimeline(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
  const { data: changeLogsPersisted = [], isLoading: changeLogsLoading } = useQuery({
    queryKey: ['change-logs', 'INVESTOR', id],
    queryFn: () => api.getChangeLogs({ entityId: id!, entityType: 'INVESTOR', limit: 200 }),
    enabled: !!id,
    staleTime: 30_000,
  });
  const historyRows = useMemo(() => {
    if (!id) return [];
    return mergeHistoryRows(
      mapTimelineToChangeLogEntries(id, 'INVESTOR', investorTimeline),
      changeLogsPersisted,
    );
  }, [id, investorTimeline, changeLogsPersisted]);
  const historyLoading = investorTimelineLoading || changeLogsLoading;

  const investor: Investor | null = useMemo(() => {
    if (!investorData) return null;
    return {
      id: investorData.id,
      name: investorData.name,
      email: investorData.email,
      additionalEmails:
        investorData.additionalEmails ??
        (investorData.additional_emails ?? []).map((r: { email: string }) => r.email),
      phone: investorData.phone || '',
      status: investorData.status || 'active',
      type: investorData.type || 'fisica',
      totalInvested: investorData.totalInvested || 0,
      propertiesCount: investorData.propertiesCount || 0,
      curp: investorData.curp,
      csf: investorData.csf,
      certificadoFIEL: investorData.certificadoFIEL,
      idOficial: investorData.idOficial,
      kyc: investorData.kyc,
      caratulaBancaria: investorData.caratulaBancaria,
      curpRepLegal: investorData.curpRepLegal,
      actaConstitutiva: investorData.actaConstitutiva,
      documents: investorData.investor_documents ?? investorData.documents ?? [],
      createdAt: investorData.createdAt || new Date().toISOString(),
    };
  }, [investorData]);

  const investments: Investment[] = useMemo(() => {
    if (!investorData?.investments) return [];
    return (investorData.investments || []).map((inv: any) => {
      const propertyName = inv.property?.name || inv.properties?.name || '';
      const property = properties.find((p: any) => p.id === inv.propertyId);
      const finalPropertyName = propertyName || property?.name || '';
      return {
        id: inv.id,
        investorId: inv.investorId,
        propertyId: inv.propertyId,
        propertyName: finalPropertyName,
        amount: inv.amount,
        ownershipPct: inv.ownershipPct,
        status: inv.status,
        startDate: inv.startDate,
        endDate: inv.endDate,
        notes: inv.notes,
        createdAt: inv.createdAt,
        unitIds: inv.unitIds ?? [],
        units: inv.units ?? [],
      };
    });
  }, [investorData, properties]);

  const queryErrorMessage = queryError ? (queryError as Error)?.message ?? 'Error al cargar el inversionista' : null;
  const error = mutationError || queryErrorMessage;

  // Combinar documentos de investor_documents con archivos de campos del investor
  const allDocuments = useMemo(() => {
    if (!investor) return [];
    
    const API_BASE_URL = getViteApiUrl();
    
    // Documentos de la tabla investor_documents
    const tableDocuments = (investor.documents || []).map((doc: any) => ({
      id: doc.id,
      title: doc.title,
      fileUrl: doc.fileUrl,
      fileType: doc.fileType,
      uploadedAt: doc.uploadedAt,
      documentType: doc.documentType,
      expiresAt: doc.expiresAt,
    }));
    
    // Archivos de campos del investor
    const fieldDocuments: Array<{
      id: string;
      title: string;
      fileUrl: string;
      fileType?: string;
      uploadedAt: string;
    }> = [];
    
    // Mapeo de campos a nombres de documento
    const fieldMappings = [
      { field: 'csf', title: 'CSF (Constancia de Situación Fiscal)' },
      { field: 'idOficial', title: 'ID Oficial' },
      { field: 'kyc', title: 'KYC (Know Your Customer)' },
      { field: 'caratulaBancaria', title: 'Carátula Bancaria' },
      { field: 'actaConstitutiva', title: 'Acta Constitutiva' },
      { field: 'certificadoFIEL', title: 'Certificado FIEL' },
    ];
    
    fieldMappings.forEach(({ field, title }) => {
      const filePath = investor[field as keyof Investor] as string | undefined;
      if (filePath && filePath.trim()) {
        // Construir URL completa: si empieza con /, agregar /uploads y la URL base
        const fileUrl = filePath.startsWith('/') 
          ? `${API_BASE_URL}/uploads${filePath}`
          : filePath;
        
        // Obtener extensión del archivo para determinar el tipo
        const extension = filePath.split('.').pop()?.toLowerCase();
        const fileType = extension === 'pdf' ? 'pdf' : 
                        ['jpg', 'jpeg', 'png'].includes(extension || '') ? 'image' : 
                        'doc';
        
        fieldDocuments.push({
          id: `field-${field}-${investor.id}`,
          title,
          fileUrl,
          fileType,
          uploadedAt: investor.createdAt, // Usar fecha de creación como fecha de subida aproximada
        });
      }
    });
    
    return [...tableDocuments, ...fieldDocuments];
  }, [investor]);

  const isDeletableInvestorDocument = (documentId: string) =>
    !documentId.startsWith('field-');

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !addDocForm.title.trim()) { setAddDocError('El título es requerido.'); return; }
    setAddDocLoading(true);
    setAddDocError(null);
    try {
      let fileUrl: string | undefined;
      if (addDocFile) {
        const res = await api.uploadPropertyDocument(addDocFile);
        fileUrl = res.fileUrl;
      }
      await api.addInvestorDocument(id, {
        title: addDocForm.title.trim(),
        fileUrl,
        fileType: addDocForm.fileType || undefined,
        expiresAt: addDocForm.expiresAt || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['investor', id] });
      setAddDocModalOpen(false);
      setAddDocForm({ title: '', fileType: '', expiresAt: '' });
      setAddDocFile(null);
      setToast({ message: 'Documento agregado correctamente.', type: 'success' });
    } catch (err: any) {
      setAddDocError(err?.message || 'Error al agregar el documento');
    } finally {
      setAddDocLoading(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (!pendingDeleteDocument || !id) return;
    setDocumentDeleteLoading(true);
    setMutationError(null);
    try {
      await api.removeInvestorDocument(pendingDeleteDocument.id);
      await queryClient.invalidateQueries({ queryKey: ['investor', id] });
      setPendingDeleteDocument(null);
    } catch (err: any) {
      setMutationError(err.message || 'Error al eliminar documento');
    } finally {
      setDocumentDeleteLoading(false);
    }
  };

  const summaryData = useMemo(() => {
    const r = roiSummary as Record<string, unknown> | null | undefined;
    if (!r || typeof r !== 'object') {
      return {
        totalAccumulatedIncome: 0,
        totalNetFlow: 0,
        totalGain: 0,
        averageAnnualROI: 0,
        totalInitialInvestment: 0,
      };
    }
    const totalReceived = Number(r.totalReceived ?? 0);
    const totalInvested = Number(r.totalInvested ?? 0);
    const overallROI = Number(r.overallROI ?? 0);
    const netFlow = Number(r.totalNetFlow ?? r.netFlow ?? totalReceived);
    const totalGain = Number(r.totalGain ?? Math.max(0, totalReceived - totalInvested));
    return {
      totalAccumulatedIncome: totalReceived,
      totalNetFlow: netFlow,
      totalGain,
      averageAnnualROI: overallROI,
      totalInitialInvestment: totalInvested,
    };
  }, [roiSummary]);

  const activeInvestments = investments.filter(
    (inv) => inv.investorId === id && inv.status === 'active'
  );
  const historicalInvestments = investments.filter(
    (inv) => inv.investorId === id && inv.status !== 'active'
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-gray-500 mt-4">Cargando inversionista...</p>
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{error || 'Inversionista no encontrado'}</p>
        <Button onClick={() => navigate('/inversionistas')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenEditInvestmentModal = (inv: Investment) => {
    setEditingInvestment(inv);
    setIsEditInvestmentModalOpen(true);
  };

  const handleCloseEditInvestmentModal = () => {
    setIsEditInvestmentModalOpen(false);
    setEditingInvestment(null);
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditSubmit = async (
    investorData: Omit<Investor, 'id' | 'createdAt' | 'totalInvested' | 'propertiesCount'>,
    files?: {
      csf?: File | null;
      idOficial?: File | null;
      kyc?: File | null;
      caratulaBancaria?: File | null;
      actaConstitutiva?: File | null;
    },
  ) => {
    if (!id) return;
    try {
      setInvestorEditLoading(true);
      setMutationError(null);
      await api.updateInvestor(id, investorData, files);
      await queryClient.invalidateQueries({ queryKey: ['investor', id] });
      await queryClient.invalidateQueries({ queryKey: ['investors'] });
      await queryClient.invalidateQueries({ queryKey: ['investor-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'INVESTOR', id] });
      handleCloseEditModal();
    } catch (err: any) {
      setMutationError(err?.message || 'Error al actualizar el inversionista');
    } finally {
      setInvestorEditLoading(false);
    }
  };

  const handleSubmit = async (investmentData: Omit<Investment, 'id' | 'createdAt' | 'investorName' | 'propertyName'>) => {
    if (!id) return;
    try {
      setInvestmentSubmitLoading(true);
      setMutationError(null);
      const investmentPayload: Record<string, unknown> = {
        investorId: id,
        propertyId: investmentData.propertyId,
        amount: Number(investmentData.amount),
        ownershipPct: Number(investmentData.ownershipPct),
        startDate: investmentData.startDate,
        status: investmentData.status || 'active',
      };
      if (investmentData.endDate) {
        investmentPayload.endDate = investmentData.endDate;
      }
      if (investmentData.notes?.trim()) {
        investmentPayload.notes = investmentData.notes.trim();
      }
      if (investmentData.unitIds) {
        investmentPayload.unitIds = investmentData.unitIds;
      }
      await api.createInvestment(investmentPayload);
      queryClient.invalidateQueries({ queryKey: ['investor', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-roi', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-statements', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['change-logs', 'INVESTOR', id] });
      handleCloseModal();
    } catch (err: any) {
      console.error('Error creating investment:', err);
      setMutationError(err.message || 'Error al crear la inversión');
    } finally {
      setInvestmentSubmitLoading(false);
    }
  };

  const handleUpdateInvestment = async (
    investmentData: Omit<
      Investment,
      'id' | 'createdAt' | 'investorName' | 'propertyName'
    >,
  ) => {
    if (!id || !editingInvestment) return;
    try {
      setInvestmentSubmitLoading(true);
      setMutationError(null);
      const payload: Record<string, unknown> = {
        propertyId: investmentData.propertyId,
        amount: Number(investmentData.amount),
        ownershipPct: Number(investmentData.ownershipPct),
        startDate: investmentData.startDate,
        status: investmentData.status || editingInvestment.status || 'active',
      };
      if (investmentData.endDate) {
        payload.endDate = investmentData.endDate;
      }
      if (investmentData.notes?.trim()) {
        payload.notes = investmentData.notes.trim();
      }
      payload.unitIds = investmentData.unitIds ?? [];
      await api.updateInvestment(editingInvestment.id, payload);
      queryClient.invalidateQueries({ queryKey: ['investor', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-roi', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-statements', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-timeline', id] });
      queryClient.invalidateQueries({ queryKey: ['change-logs', 'INVESTOR', id] });
      handleCloseEditInvestmentModal();
      setToast({ message: 'Inversión actualizada exitosamente', type: 'success' });
    } catch (err: any) {
      setToast({ message: err?.message || 'Error al actualizar la inversión', type: 'error' });
      setMutationError(err?.message || 'Error al actualizar la inversión');
    } finally {
      setInvestmentSubmitLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferringInvestment || !targetInvestorId) return;
    setTransferLoading(true);
    try {
      await api.updateInvestment(transferringInvestment.id, { investorId: targetInvestorId });
      queryClient.setQueryData(['investor', id], (old: any) =>
        old
          ? { ...old, investments: (old.investments || []).filter((inv: any) => inv.id !== transferringInvestment.id) }
          : old,
      );
      await queryClient.invalidateQueries({ queryKey: ['investor', id] });
      queryClient.invalidateQueries({ queryKey: ['investor-roi', id] });
      setTransferringInvestment(null);
      setTargetInvestorId('');
      setToast({ message: 'Inversión transferida exitosamente', type: 'success' });
    } catch (e: any) {
      setToast({ message: e?.message || 'No se pudo transferir la inversión.', type: 'error' });
    } finally {
      setTransferLoading(false);
    }
  };

  const formatFechaCorta = formatFechaCortaUtil;
  const formatMoneda = (n: number) =>
    canViewPrices
      ? n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 2 })
      : '—';

  const investmentColumns: Column<Investment>[] = [
    { header: 'Propiedad', accessor: 'propertyName' },
    {
      header: 'Monto',
      accessor: (row) => formatMoneda(Number(row.amount)),
    },
    {
      header: 'Participación',
      accessor: (row) => `${row.ownershipPct}%`,
    },
    {
      header: 'Unidades',
      accessor: (row) => {
        const count = (row.units ?? []).length;
        if (count === 0) return <span className="text-gray-400 text-xs">—</span>;
        return <span className="text-xs font-medium text-gray-700">{count} unidad{count !== 1 ? 'es' : ''}</span>;
      },
    },
    {
      header: 'Fecha de Inversión',
      accessor: (row) => formatFechaCorta(row.createdAt),
    },
    {
      header: 'Fecha Inicio',
      accessor: (row) => formatFechaCorta(row.startDate),
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'completed'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status === 'active'
            ? 'Activa'
            : row.status === 'completed'
            ? 'Completada'
            : 'Cancelada'}
        </span>
      ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleOpenEditInvestmentModal(row)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium"
          >
            <Edit className="w-3.5 h-3.5" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => { setTransferringInvestment(row); setTargetInvestorId(''); }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs font-medium"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            Transferir
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`¿Eliminar permanentemente esta inversión en ${row.propertyName}?\n\nEsta acción no se puede deshacer.`)) return;
              try {
                await api.deleteInvestment(row.id);
                // Actualiza el cache de inmediato para que la tabla refleje el cambio sin esperar el refetch
                queryClient.setQueryData(['investor', id], (old: any) =>
                  old
                    ? {
                        ...old,
                        investments: (old.investments || []).filter((inv: any) => inv.id !== row.id),
                      }
                    : old,
                );
                await queryClient.invalidateQueries({ queryKey: ['investor', id] });
                queryClient.invalidateQueries({ queryKey: ['investor-roi', id] });
                queryClient.invalidateQueries({ queryKey: ['investor-timeline', id] });
                setToast({ message: 'Inversión eliminada exitosamente', type: 'success' });
              } catch (e: any) {
                setToast({ message: e?.message || 'No se pudo eliminar la inversión.', type: 'error' });
              }
            }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/inversionistas')}>
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{investor.name}</h1>
        </div>
        <Button variant="secondary" icon={Edit} onClick={handleOpenEditModal}>
          Editar Inversionista
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'general', label: 'Información General' },
            { id: 'investments', label: 'Inversiones Activas' },
            { id: 'documents', label: 'Documentos' },
            { id: 'statements', label: 'Estado de Cuenta' },
            { id: 'roi', label: 'ROI' },
            { id: 'history', label: 'Historial' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Información de Contacto">
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Nombre</label>
                <p className="text-base font-medium">{investor.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Correo principal</label>
                <p className="text-base font-medium">{investor.email}</p>
              </div>
              {(investor.additionalEmails?.length ?? 0) > 0 && (
                <div>
                  <label className="text-sm text-gray-600">Correos adicionales</label>
                  <ul className="mt-1 space-y-1">
                    {investor.additionalEmails!.map((addr) => (
                      <li key={addr} className="text-base font-medium text-gray-800">
                        {addr}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-600">Teléfono</label>
                <p className="text-base font-medium">{investor.phone}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Estado</label>
                <p className="text-base font-medium">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      investor.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {investor.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Fecha de Registro</label>
                <p className="text-base font-medium">
                  {formatDateSpanishLong(investor.createdAt)}
                </p>
              </div>
            </div>
          </Card>

          <Card title="Resumen de Inversiones">
            <div className="space-y-5">
              <div>
                <label className="text-sm text-gray-600">Total Invertido</label>
                <p className="text-2xl font-bold text-primary">
                  {formatMoneda(Number(investor.totalInvested || 0))}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Propiedades</label>
                <p className="text-2xl font-bold text-primary">{investor.propertiesCount}</p>
              </div>
              <div className="pt-5 border-t border-gray-200">
                <label className="text-sm text-gray-600">Ingreso Acumulado del Año</label>
                <p className="text-xl font-bold text-green-600">
                  {formatMoneda(summaryData.totalAccumulatedIncome)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Flujo Neto Total Recibido</label>
                <p className="text-xl font-bold text-primary">
                  {formatMoneda(summaryData.totalNetFlow)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Ganancia Total</label>
                <p className="text-xl font-bold text-primary">
                  {formatMoneda(summaryData.totalGain)}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">ROI Promedio Anual</label>
                <p className="text-xl font-bold text-primary">
                  {summaryData.averageAnnualROI.toFixed(2)}%
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'investments' && (
        <div className="space-y-6">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Inversiones Activas</h2>
              <Button icon={Plus} onClick={handleOpenModal}>
                Agregar Inversión
              </Button>
            </div>
            <Card>
              {activeInvestments.length > 0
                ? <Table data={activeInvestments} columns={investmentColumns} />
                : <p className="text-center text-gray-500 py-8 text-sm">Sin inversiones activas.</p>
              }
            </Card>
          </div>
          {historicalInvestments.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-gray-600 mb-3">Inversiones históricas</h3>
              <Card>
                <Table data={historicalInvestments} columns={investmentColumns} />
              </Card>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">Documentos</h2>
              <p className="text-sm text-gray-500 mt-1">
                Documentos adjuntos a este inversionista. Estado: vigente, por vencer o vencido.
              </p>
            </div>
            <Button icon={Plus} onClick={() => setAddDocModalOpen(true)}>
              Agregar documento
            </Button>
          </div>
          <Card>
            {allDocuments && allDocuments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allDocuments.map((document: any) => {
                  const docStatus = getDocumentStatus(document.expiresAt);
                  const kindLabel = document.fileType?.toUpperCase?.() || 'DOC';
                  const dateLabel = document.uploadedAt
                    ? formatDate(document.uploadedAt)
                    : '';
                  const canDeleteDoc =
                    canDeleteInvestors && isDeletableInvestorDocument(document.id);
                  return (
                    <div
                      key={document.id}
                      className="relative space-y-2 rounded-lg border border-gray-200 bg-white p-3 hover:border-primary/30 hover:shadow-sm transition-all"
                    >
                      {canDeleteDoc && (
                        <button
                          type="button"
                          onClick={() =>
                            setPendingDeleteDocument({
                              id: document.id,
                              title: document.title,
                            })
                          }
                          className="absolute top-2 right-2 z-10 p-2 rounded-lg text-red-600 bg-white/95 border border-gray-100 shadow-sm hover:bg-red-50 transition-colors"
                          title="Eliminar documento"
                          aria-label="Eliminar documento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <div className="flex items-center justify-between gap-3 pr-10">
                        <div className="flex flex-wrap items-center gap-2">
                          <DocumentStatusBadge status={docStatus} />
                          {document.documentType && (
                            <span className="text-xs text-gray-500 capitalize">
                              {String(document.documentType).replace(/_/g, ' ')}
                            </span>
                          )}
                          {document.expiresAt && (
                            <span className="text-xs text-gray-500">
                              Vence: {formatFechaCortaUtil(document.expiresAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      {document.fileUrl ? (
                        <DocumentViewer
                          title={document.title}
                          fileUrl={document.fileUrl}
                          fileTypeLabel={[kindLabel, dateLabel].filter(Boolean).join(' · ')}
                          downloadName={document.title ? `${document.title}` : undefined}
                          className="border-0 shadow-none hover:shadow-none p-0"
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No hay documentos adjuntos a este inversionista</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'statements' && investor && (
        <InvestorAccountStatementsTab investorId={investor.id} activeInvestments={activeInvestments} />
      )}

      {activeTab === 'roi' && investor && <InvestorRoiTab investorId={investor.id} />}

      {activeTab === 'history' && investor && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Historial de Cambios</h2>
          <HistoryTable rows={historyRows} loading={historyLoading} />
        </div>
      )}

      {/* Modal para Agregar Inversión */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Agregar Nueva Inversión"
        size="lg"
      >
        {isModalOpen && (
          <InvestmentForm
            key="add-investment"
            investorId={id || ''}
            investorName={investor?.name || ''}
            existingInvestments={investments}
            properties={properties}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={investmentSubmitLoading}
          />
        )}
      </Modal>

      {/* Modal para Editar Inversión */}
      <Modal
        isOpen={isEditInvestmentModalOpen}
        onClose={handleCloseEditInvestmentModal}
        title="Editar Inversión"
        size="lg"
      >
        <InvestmentForm
          investorId={id || ''}
          investorName={investor?.name || ''}
          initialInvestment={editingInvestment}
          existingInvestments={investments.filter(
            (inv) => inv.id !== editingInvestment?.id,
          )}
          properties={properties}
          onSubmit={handleUpdateInvestment}
          onCancel={handleCloseEditInvestmentModal}
          submitLabel="Guardar cambios"
          isLoading={investmentSubmitLoading}
        />
      </Modal>

      {/* Modal para Editar Inversionista */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Editar Inversionista"
        size="xl"
      >
        <InvestorForm
          investor={investor}
          onSubmit={handleEditSubmit}
          onCancel={handleCloseEditModal}
          isLoading={investorEditLoading}
        />
      </Modal>

      <ConfirmDeleteModal
        isOpen={pendingDeleteDocument !== null}
        onClose={() => !documentDeleteLoading && setPendingDeleteDocument(null)}
        onConfirm={handleRemoveDocument}
        title="Eliminar documento"
        message={
          pendingDeleteDocument ? (
            <>
              ¿Eliminar el documento <strong>{pendingDeleteDocument.title}</strong> del
              expediente de este inversionista?
            </>
          ) : null
        }
        isLoading={documentDeleteLoading}
      />

      {/* Modal Transferir Inversión */}
      <Modal
        isOpen={transferringInvestment !== null}
        onClose={() => { if (!transferLoading) { setTransferringInvestment(null); setTargetInvestorId(''); } }}
        title="Transferir Inversión"
        size="sm"
      >
        {transferringInvestment && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reasigna la inversión en{' '}
              <strong className="text-gray-900">{transferringInvestment.propertyName}</strong>{' '}
              a otro inversionista. El monto, porcentaje y fecha de inicio se conservan.
            </p>
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Monto</span>
                <span className="font-medium">{formatMoneda(Number(transferringInvestment.amount))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Participación</span>
                <span className="font-medium">{transferringInvestment.ownershipPct}%</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nuevo inversionista
              </label>
              <InvestorSelect
                investors={otherInvestors}
                value={targetInvestorId}
                onChange={setTargetInvestorId}
                disabled={transferLoading}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
              <Button
                variant="secondary"
                onClick={() => { setTransferringInvestment(null); setTargetInvestorId(''); }}
                disabled={transferLoading}
              >
                Cancelar
              </Button>
              <Button onClick={handleTransfer} disabled={!targetInvestorId || transferLoading}>
                {transferLoading ? 'Transfiriendo…' : 'Confirmar transferencia'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Agregar Documento */}
      <Modal
        isOpen={addDocModalOpen}
        onClose={() => { if (!addDocLoading) { setAddDocModalOpen(false); setAddDocError(null); } }}
        title="Agregar documento"
        size="sm"
      >
        <form onSubmit={handleAddDocument} className="space-y-4">
          {addDocError && <p className="text-sm text-red-600">{addDocError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              required
              value={addDocForm.title}
              onChange={(e) => setAddDocForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Ej: INE, Constancia fiscal…"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setAddDocFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de vencimiento (opcional)</label>
            <input
              type="date"
              value={addDocForm.expiresAt}
              onChange={(e) => setAddDocForm((f) => ({ ...f, expiresAt: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setAddDocModalOpen(false)} disabled={addDocLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={addDocLoading}>
              {addDocLoading ? 'Guardando…' : 'Guardar documento'}
            </Button>
          </div>
        </form>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

